import React from 'react';
import { X, Edit2 } from 'lucide-react';
import LightboxGallery from './LightboxGallery';

// ─── Types ───────────────────────────────────────────────────────────────────

interface MedicalDevice {
  id: number;
  tracking_id: string;
  reference_id: string | null;
  item_name: string;
  category: string | null;
  priority: number | null;
  status: string;
  purchase_date: string | null;
  purchase_cost: number | null;
  monthly_rental_rate: number | null;
  daily_rental_rate: number | null;
  gst_applicable: string | null;
  photo_1_url: string | null;
  photo_2_url: string | null;
  photo_3_url: string | null;
  photo_4_url: string | null;
  photo_5_url: string | null;
}

interface Props {
  device: MedicalDevice;
  onClose: () => void;
  onEdit: () => void;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const fmtDate = (d?: string | null) => {
  if (!d) return '-';
  const [y, m, day] = d.split('T')[0].split('-');
  return `${day}-${m}-${y}`;
};

const fmtCurrency = (v?: number | null) =>
  v != null ? `₹${v.toFixed(2)}` : '-';

const getStatusColor = (status: string): React.CSSProperties => {
  const map: Record<string, React.CSSProperties> = {
    'Functional':   { background: '#dcfce7', color: '#166534' },
    'Rented Out':   { background: '#dbeafe', color: '#1e40af' },
    'In Repair':    { background: '#ffedd5', color: '#c2410c' },
    'Not Working':  { background: '#fee2e2', color: '#b91c1c' },
  };
  return map[status] ?? { background: '#f1f5f9', color: '#475569' };
};

// ─── Shared sub-styles ────────────────────────────────────────────────────────

const panelHeader: React.CSSProperties = {
  background: '#e8f0f7',
  borderBottom: '1px solid #d0dce8',
  padding: '0.5rem 1rem',
  fontSize: '0.67rem',
  fontWeight: 800,
  color: '#2d4a6b',
  textTransform: 'uppercase',
  letterSpacing: '0.1em',
};

const infoLabel: React.CSSProperties = {
  fontSize: '0.72rem', color: '#94a3b8', fontWeight: 700,
  textTransform: 'uppercase', letterSpacing: '0.05em', minWidth: 160,
};

const infoValue: React.CSSProperties = {
  fontSize: '0.82rem', color: '#1e293b', fontWeight: 500,
};

// ─── Info Row ─────────────────────────────────────────────────────────────────

const InfoRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div style={{ display: 'flex', alignItems: 'baseline', padding: '0.45rem 1rem', borderBottom: '1px solid #f1f5f9', gap: '0.75rem' }}>
    <span style={infoLabel}>{label}</span>
    <span style={infoValue}>{value ?? '-'}</span>
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const MedicalDeviceDetailsModal: React.FC<Props> = ({ device, onClose, onEdit }) => {
  const statusStyle = getStatusColor(device.status);

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(15,23,42,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(4px)' }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ backgroundColor: 'white', borderRadius: '12px', width: '98%', maxWidth: '860px', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' }}
      >
        {/* ── Header ── */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.9rem 1.25rem', borderBottom: '1px solid #e2e8f0', flexShrink: 0, gap: '1rem' }}>
          <div style={{ minWidth: 0 }}>
            <p style={{ margin: 0, fontSize: '0.68rem', fontWeight: 700, color: '#3b82f6', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Medical Device Details</p>
            <h2 style={{ margin: '0.1rem 0 0', fontSize: '1.15rem', fontWeight: 800, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {device.item_name}
            </h2>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexShrink: 0 }}>
            <button
              onClick={onEdit}
              style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', padding: '0.38rem 0.85rem', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '6px', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 700, color: '#475569' }}
            >
              <Edit2 size={13} /> Edit Details
            </button>
            <button
              onClick={onClose}
              style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: '6px', cursor: 'pointer', padding: '0.38rem', display: 'flex', color: '#64748b' }}
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* ── Body ── */}
        <div style={{ overflowY: 'auto' }}>

          {/* ── Top Panel Grid ── */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', borderBottom: '1px solid #e2e8f0' }}>

            {/* Panel 1 — Asset Details */}
            <div style={{ borderRight: '1px solid #e2e8f0' }}>
              <div style={panelHeader}>Asset Details</div>
              <InfoRow label="Category" value={device.category || 'Uncategorized'} />
              <InfoRow label="Priority" value={device.priority ?? 'Not set'} />
              <InfoRow label="Reference ID" value={
                <span style={{ fontFamily: 'monospace', fontWeight: 700 }}>{device.reference_id || 'Not Assigned'}</span>
              } />
              <InfoRow label="Status" value={
                <span style={{ ...statusStyle, padding: '0.1rem 0.5rem', borderRadius: '999px', fontSize: '0.72rem', fontWeight: 700 }}>
                  {device.status}
                </span>
              } />
            </div>

            {/* Panel 2 — Financial Details */}
            <div>
              <div style={panelHeader}>Financial Details</div>
              <InfoRow label="Purchase Date"   value={fmtDate(device.purchase_date)} />
              <InfoRow label="Purchase Cost"   value={fmtCurrency(device.purchase_cost)} />
              <InfoRow label="Monthly Rental"  value={fmtCurrency(device.monthly_rental_rate)} />
              <InfoRow label="Daily Rental"    value={fmtCurrency(device.daily_rental_rate)} />
              <InfoRow label="GST Applicable"  value={device.gst_applicable || 'N/A'} />
            </div>
          </div>

          {/* ── Photos Section ── */}
          {(device.photo_1_url || device.photo_2_url || device.photo_3_url || device.photo_4_url || device.photo_5_url) && (
            <div>
              <div style={panelHeader}>Photos</div>
              <div style={{ padding: '0.75rem 1rem' }}>
                <LightboxGallery
                  images={[device.photo_1_url, device.photo_2_url, device.photo_3_url, device.photo_4_url, device.photo_5_url]}
                  thumbnailSize={72}
                  alt="Device photo"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MedicalDeviceDetailsModal;
